'use client';
import { Button, FlexWrapper, FontIcon, MenuList, CustomScroll, Text } from '@intigral/shared';
import { MenuItem } from '@intigral/shared/components/MenuList';
import cn from 'classnames';
import { SyntheticEvent, useEffect, useState } from 'react';
import { Search } from '../../Search';
import { ChannelCard } from '../ChannelCard';
import styles from './channels-search.module.sass';
import { Channel } from 'planby/dist/Epg/helpers/interfaces';
import { useMediaQuery } from '@intigral/hooks/dom/useMediaQuery/useMediaQuery';

type Category = {
    name: string;
    label: string;
    isToday?: boolean;
    count: number;
};
type Date = {
    name: string;
    label: string;
    isToday?: boolean;
};
interface IChannelSearch {
    searchLabel: string;
    channels: any[];
    noResultsMsg: string;
    channelTypes: Category[];
    typeSelect: (typeName: string) => void;
    channelSelect: (selectedChannel: Channel, keyword: string) => void;
    selectedType: string;
    textChange: (search: string) => void;
    openCallback?: () => void;
    enableLockIcon?: boolean;
    checkShowLockIcon?: any;
    onClickLock?: () => void;
    days: Date[];
    selectedDay: string;
    daySelect: (date: string) => void;
    goLiveAction?: () => void; // optional type for backwards compatibiliy with LiveTV and NewLiveTV
    isLive?: boolean;
    // setIsLive?: React.Dispatch<React.SetStateAction<boolean>>;
    liveButtonLabel?: string;
    localization?: {
        select_date: string;
        choose_category: string;
        date?: string;
        clearLabel?: string;
    };
    /** Used to change `Search Channels` btn appearance when ad is playing */
    isClientSideAdPlaying?: boolean;
}

export const ChannelsSearch = ({
    searchLabel,
    channels,
    noResultsMsg,
    channelTypes,
    typeSelect,
    channelSelect,
    selectedType,
    textChange,
    openCallback,
    enableLockIcon,
    checkShowLockIcon,
    onClickLock,
    days,
    selectedDay,
    daySelect,
    goLiveAction,
    isLive,
    liveButtonLabel = 'Live',
    localization,
    isClientSideAdPlaying = false,
}: IChannelSearch) => {
    const [isOpen, setIsOpen] = useState(false);
    const [dayOpen, setDayOpen] = useState<boolean>(false);
    const [selectedTypeState, setSelectedType] = useState<string>(selectedType || '');
    const [selectedDayState, setSelectedDay] = useState<string>(selectedDay || '');
    const [query, setQuery] = useState<string>('');
    const isMobile = useMediaQuery('(max-width: 924px)');

    const getDateLabel = () => {
        const selectedDate = days.find((type) => type.name === selectedDayState);
        if (!selectedDate) return '';
        return selectedDate.isToday ? localization?.date : selectedDate.label;
    };

    useEffect(() => {
        textChange(query);
    }, [query]);

    useEffect(() => {
        setSelectedType(selectedType);
        setSelectedDay(selectedDay);
    }, [selectedType, selectedDay]);

    return (
        <div className="container full-width">
            <FlexWrapper justifyContent="between" gap="0" className={styles.holder}>
                {!(isClientSideAdPlaying && isMobile) && (
                    <Button
                        className={cn(styles.btnsearch, styles.filter, { [styles.disabled]: isClientSideAdPlaying })}
                        secondary
                        onClick={() => {
                            setIsOpen(true);
                            if (typeof openCallback === 'function') {
                                openCallback();
                            }
                        }}
                        data-test-id="search-button"
                    >
                        <FontIcon md="smaller" name="search" />
                        &nbsp; {searchLabel}
                    </Button>
                )}
                <FlexWrapper flexDirection="row" gap="1x">
                    {/* Day selecting part */}
                    <MenuList
                        triggerTitle={getDateLabel()}
                        dropIcon="calendar"
                        border
                        selectedItem={selectedDayState}
                        data-test-id="days-button"
                        variant="placeholder"
                        placeholder={localization?.select_date}
                        className={styles.filter}
                        width="full"
                    >
                        <CustomScroll hidden>
                            {days.map((ct: any, index: number) => {
                                return (
                                    <MenuItem
                                        key={ct.name + index}
                                        onClick={(e: SyntheticEvent<Element, Event>) => {
                                            setDayOpen(false);
                                            setSelectedDay(ct.name);
                                            daySelect(ct.name);
                                        }}
                                        className={cn(styles['menu-item'], {
                                            [styles['menu-item--selected']]: selectedDayState === ct.name,
                                        })}
                                        // {...currentDay && {className: styles.today}} - uncomment this to highlight Today
                                    >
                                        {ct?.isToday ? `${ct.label} ${localization?.date || ''}` : ct.label}
                                    </MenuItem>
                                );
                            })}
                        </CustomScroll>
                    </MenuList>

                    {/* Category (type) selecting part */}
                    <MenuList
                        triggerTitle={channelTypes.filter((type) => type.name === selectedTypeState)[0]?.label || ''}
                        dropIcon="filters"
                        border
                        selectedItem={selectedTypeState}
                        data-test-id="geners-button"
                        variant="placeholder"
                        placeholder={localization?.choose_category}
                        className={styles.filter}
                    >
                        {channelTypes.map((ct: Category, index: number) => {
                            return (
                                <MenuItem
                                    key={ct.name + index}
                                    onClick={(e: SyntheticEvent<Element, Event>) => {
                                        if (ct?.count > 0) {
                                            setIsOpen(false);
                                            setSelectedType(ct.name);
                                            typeSelect(ct.name);
                                        }
                                    }}
                                    className={cn(styles['menu-item'], {
                                        [styles['menu-item--selected']]: selectedTypeState === ct.name,
                                    })}
                                >
                                    {ct.label}
                                </MenuItem>
                            );
                        })}
                    </MenuList>

                    {/* LiveTV toggle button */}
                    <Button
                        secondary
                        className={cn(styles.filter, styles.live, !isLive && styles.active)}
                        onClick={() => {
                            goLiveAction?.();
                        }}
                    >
                        {!isMobile && (
                            <Text as="span" variant="text3">
                                {liveButtonLabel}
                            </Text>
                        )}
                        {isMobile ? <FontIcon name="live-text" /> : <FontIcon md="smaller" name="live-tv" />}
                    </Button>
                </FlexWrapper>
            </FlexWrapper>
            {isOpen && (
                <Search
                    open={isOpen}
                    onOpenChange={() => setIsOpen(false)}
                    query={query}
                    setQuery={setQuery}
                    clearLabel={localization?.clearLabel}
                >
                    {channels.length ? (
                        <div className={styles.grid}>
                            {channels.map((c, i) => {
                                return (
                                    <div key={i} className={styles.item}>
                                        <ChannelCard
                                            key={`channel-card-${i}`}
                                            index={c.index ?? i}
                                            logoSrc={c.logoSrc}
                                            title={c.title ?? ''}
                                            channelNo={c.channelNo}
                                            favourite={c.favourite}
                                            onChannelSelect={() => undefined}
                                            onClick={() => {
                                                setIsOpen(false);
                                                channelSelect(c, query);
                                                setQuery('');
                                            }}
                                            isSelected={c.isSelected}
                                            bottomLabel={c.rewindDuration}
                                            isLocked={c?.locked}
                                            onClickLock={onClickLock}
                                            ratio={2.7}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        noResultsMsg
                    )}
                </Search>
            )}
        </div>
    );
};
